<template>
  <section ref="packagesSection" class="packages">
    <div class="container">
      <h2 class="section-title">Our Packages</h2>
      <div class="packages-grid">
        <div
          v-for="(pkg, i) in packages"
          :key="i"
          class="package-card"
          :class="{ featured: pkg.featured }"
          :style="{ 'animation-delay': `${i * 0.1}s` }"
          @click="$router.push(`/Package/${pkg._id}`)"
        >
          <div class="card-bg-shape"></div>
          <div class="floating-shapes">
            <div class="triangle t1"></div>
            <div class="circle c1"></div>
          </div>
          <div v-if="pkg.saved_amount" class="save-badge">
            <span style="padding-top: 18px" class="save-label">Save</span>
            <span class="save-value">${{ pkg.saved_amount }}</span>
          </div>
          <div v-if="pkg.mostPopular" class="popular-badge">
            <span class="popular-text">Most Popular</span>
            <div class="popular-shine"></div>
          </div>
          <div class="package-content">
            <div class="package-header">
              <h3 class="package-name">{{ pkg.main_title }}</h3>
              <div class="package-frequency">{{ pkg.services_per_year }} Times / Year</div>
            </div>
            <div class="package-price-section">
              <div class="package-price">
                <span class="price">
                  <span style="font-size: 30px"> $ </span>
                  {{ pkg?.amount_in_dollars }}</span
                >
                <span class="period">/ Year</span>
              </div>
              <div class="price-underline"></div>
            </div>

            <!-- Dynamic Features List from includes_details -->
            <div
              class="features-list"
              v-if="pkg.includes_details && pkg.includes_details.length > 0"
            >
              <!-- All Appliances Service - Static Feature -->
              <div class="feature-item" style="animation-delay: 0.1s">
                <svg class="check-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>All Appliances Service</span>
              </div>

              <!-- Dynamic features (showing up to 3 now since we added 1 static) -->
              <div
                v-for="(feature, index) in pkg.includes_details.slice(0, 3)"
                :key="index"
                class="feature-item"
                :style="{ 'animation-delay': `${0.2 + index * 0.1}s` }"
              >
                <svg class="check-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>{{ feature.includes_services }}</span>
              </div>

              <!-- Show "& more" if there are more than 3 features (since we're showing 3 + 1 static = 4 total) -->
              <div
                v-if="pkg.includes_details.length > 3"
                class="feature-item more-features"
                style="animation-delay: 0.5s"
              >
                <svg class="plus-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>& more services</span>
              </div>
            </div>

            <!-- Fallback if includes_details is not available -->
            <div class="features-list" v-else-if="pkg.includes && pkg.includes.length > 0">
              <!-- All Appliances Service - Static Feature -->
              <div class="feature-item" style="animation-delay: 0.1s">
                <svg class="check-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>All Appliances Service</span>
              </div>

              <!-- Dynamic features -->
              <div
                v-for="(feature, index) in pkg.includes.slice(0, 3)"
                :key="index"
                class="feature-item"
                :style="{ 'animation-delay': `${0.2 + index * 0.1}s` }"
              >
                <svg class="check-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>{{ feature }}</span>
              </div>

              <div
                v-if="pkg.includes.length > 3"
                class="feature-item more-features"
                style="animation-delay: 0.5s"
              >
                <svg class="plus-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>& {{ pkg.includes.length - 3 }} more services</span>
              </div>
            </div>

            <button class="package-btn">View Details</button>
          </div>
          <div class="card-glow"></div>
        </div>
      </div>

      <slot name="footer"></slot>
    </div>
  </section>
</template>

<script>
export default {
  name: 'PackageCardGrid',
  props: {
    packages: {
      type: Array,
      required: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      // Intersection Observer for package cards and section title
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in');
            }
          });
        },
        { threshold: 0.1 }
      );
      document.querySelectorAll('.package-card, .section-title').forEach((el) => {
        observer.observe(el);
      });
    });
  },
};
</script>

<style scoped>
/* Importing the required fonts/icons here */
@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&display=swap');

* {
  box-sizing: border-box;
}

.packages {
  font-family: 'Nunito Sans', sans-serif;
  padding: 25px 0;
  position: relative;
  overflow: hidden;
}

.packages::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 20% 80%, rgba(244, 101, 35, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.03) 0%, transparent 50%);
  pointer-events: none;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 1;
}

/* --- ANIMATIONS --- */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes cardSlideIn {
  from {
    opacity: 0;
    transform: translateY(60px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes bounce {
  0%,
  20%,
  53%,
  80%,
  100% {
    transform: translateY(-10px);
  }
  40%,
  43% {
    transform: translateY(-15px);
  }
  70% {
    transform: translateY(-5px);
  }
  90% {
    transform: translateY(-8px);
  }
}

@keyframes shine {
  0% {
    left: -100%;
  }
  50%,
  100% {
    left: 100%;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-12px);
  }
}

.section-title {
  font-size: 56px;
  font-weight: 800;
  background: linear-gradient(135deg, #f46523 0%, #ff8a00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  margin-bottom: 60px;
  letter-spacing: -0.03em;
  line-height: 1.1;
  opacity: 0;
  transform: translateY(30px);
  animation: fadeInUp 0.8s ease-out 0.2s forwards;
}

.packages-grid {
  display: grid;
  grid-template-columns: repeat(3, 360px);
  justify-content: center;
  gap: 40px;
  margin-bottom: 120px;
}

/* --- CARD BASE STYLES --- */
.package-card {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 32px 28px;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.1),
    0 12px 40px rgba(0, 0, 0, 0.08),
    0 16px 60px rgba(0, 0, 0, 0.06),
    0 1px 3px rgba(0, 0, 0, 0.02);
  cursor: pointer;
  opacity: 0;
  transform: translateY(60px) scale(0.95);
  animation: cardSlideIn 0.8s ease-out forwards;
  overflow: hidden;
  height: 520px;
  display: flex;
  flex-direction: column;
}

.package-card:hover {
  transform: translateY(-12px) scale(1.02);
  box-shadow:
    0 25px 60px rgba(0, 0, 0, 0.08),
    0 8px 30px rgba(244, 101, 35, 0.06);
  border-color: rgba(244, 101, 35, 0.1);
}

.package-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.package-card:hover::before {
  opacity: 1;
}

/* --- FEATURED CARD STYLES --- */
.package-card.featured {
  background: linear-gradient(135deg, rgba(255, 248, 240, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%);
  border: 2px solid rgba(244, 101, 35, 0.1);
  box-shadow:
    0 8px 40px rgba(244, 101, 35, 0.08),
    0 2px 10px rgba(244, 101, 35, 0.04);
  position: relative;
}

.package-card.featured::after {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #f46523, #ff8a00, #f46523);
  border-radius: 26px;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.package-card.featured:hover::after {
  opacity: 0.1;
}

.package-card.featured:hover {
  transform: translateY(-16px) scale(1.03);
  box-shadow:
    0 35px 80px rgba(244, 101, 35, 0.12),
    0 15px 40px rgba(244, 101, 35, 0.08);
}

/* --- CARD ELEMENTS & EFFECTS --- */
.card-bg-shape {
  position: absolute;
  top: 0;
  right: 0;
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #f46523, #ff9800);
  clip-path: polygon(40% 0%, 100% 0, 100% 60%);
  opacity: 0.1;
  transition: 0.3s;
  z-index: 1;
}

.package-card:hover .card-bg-shape {
  opacity: 0.15;
  transform: scale(1.1);
}

.card-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 120%;
  height: 120%;
  background: radial-gradient(circle, rgba(244, 101, 35, 0.02) 0%, transparent 70%);
  transform: translate(-50%, -50%) scale(0.8);
  opacity: 0;
  transition: all 0.4s ease;
  pointer-events: none;
  z-index: -1;
}

.package-card:hover .card-glow {
  transform: translate(-50%, -50%) scale(1);
  opacity: 1;
}

/* Floating Shapes */
.floating-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.floating-shapes .triangle,
.floating-shapes .circle {
  position: absolute;
  opacity: 0.1;
}

.t1 {
  top: 20px;
  right: 40px;
  border-left: 20px solid transparent;
  border-right: 20px solid transparent;
  border-bottom: 35px solid #f46523;
  animation: float 4s infinite ease-in-out;
}

.c1 {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  animation: float 6s infinite ease-in-out reverse;
  top: 50px;
  left: 50px;
  background: #f46523;
}

/* Save Badge */
.save-badge {
  position: absolute;
  top: -7px;
  right: 32px;
  background: linear-gradient(135deg, #ffd700 0%, #ffb300 100%);
  color: #1a1a1a;
  padding: 4px;
  border-radius: 16px;
  font-weight: 700;
  text-align: center;
  box-shadow:
    0 8px 25px rgba(255, 179, 0, 0.25),
    0 4px 12px rgba(255, 215, 0, 0.2);
  min-width: 95px;
  transform: translateY(-10px);
  animation: bounce 2s ease-in-out infinite;
  z-index: 10;
}

.save-label {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  opacity: 0.8;
  font-weight: 600;
}

.save-value {
  display: block;
  font-size: 16px;
  font-weight: 900;
}

.popular-badge {
  position: absolute;
  top: 0px;
  left: 50%;
  transform: translateX(-50%) translateY(-100%);
  background: linear-gradient(45deg, #3b82f6, #60a5fa);
  color: white;
  padding: 10px 24px;
  border-radius: 0 0 16px 16px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
  overflow: hidden;
  z-index: 10;
}

.popular-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: rgba(255, 255, 255, 0.3);
  transform: skewX(-20deg);
  animation: shine 2s infinite ease-in-out;
}

.package-content {
  text-align: center;
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}

.package-header {
  padding-top: 40px;
  height: 85px;
}

.package-name {
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 4px;
  margin-top: 5px;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.package-frequency {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  background: rgba(100, 116, 139, 0.1);
  padding: 4px 12px;
  border-radius: 20px;
  display: inline-block;
  position: relative;
  overflow: hidden;
  z-index: 1;
  transition: color 0.4s ease-out;
}

.package-frequency::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f46523 0%, #ff9800 100%);
  border-radius: inherit;
  z-index: -1;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.4s ease-out;
}

.package-card:hover .package-frequency {
  color: white;
}

.package-card:hover .package-frequency::after {
  transform: scaleX(1);
}

.package-price-section {
  position: relative;
  margin-top: 12px;
}

.package-price {
  padding-top: 20px;
  margin-bottom: 12px;
}

.price {
  font-size: 44px;
  font-weight: 900;
  background: linear-gradient(135deg, #f46523 0%, #ff8a00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  letter-spacing: -0.03em;
}

.period {
  font-size: 18px;
  color: #64748b;
  font-weight: 600;
  margin-left: 6px;
}

.price-underline {
  width: 50px;
  height: 3px;
  background: linear-gradient(135deg, #f46523 0%, #ff8a00 100%);
  border-radius: 2px;
  margin: 0 auto 10px;
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.package-card:hover .price-underline {
  transform: scaleX(1);
}

/* --- FEATURES LIST --- */
.features-list {
  width: 100%;
  margin: 12px 0;
  text-align: left;
  padding: 0 4px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;
  font-size: 13px;
  color: #475569;
  font-weight: 500;
  opacity: 0;
  transform: translateX(-10px);
  animation: slideInFeature 0.4s ease-out forwards;
}

@keyframes slideInFeature {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.check-icon {
  width: 16px;
  height: 16px;
  min-width: 16px;
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
  border-radius: 50%;
  padding: 3px;
  transition: all 0.3s ease;
}

.package-card:hover .check-icon {
  background: rgba(16, 185, 129, 0.2);
  transform: scale(1.1);
}

.more-features {
  color: #f46523;
  font-weight: 600;
  font-style: italic;
}

.plus-icon {
  width: 16px;
  height: 16px;
  min-width: 16px;
  color: #f46523;
  background: rgba(244, 101, 35, 0.1);
  border-radius: 50%;
  padding: 3px;
  transition: all 0.3s ease;
}

.package-card:hover .plus-icon {
  background: rgba(244, 101, 35, 0.2);
  transform: scale(1.1) rotate(90deg);
}

/* --- DIFFERENT BADGE COLORS BY PACKAGE --- */
.package-card:nth-child(2) .save-badge {
  /* Silver Badge */
  background: linear-gradient(135deg, #c0c0c0 0%, #d9d9d9 100%);
  color: #1a1a1a;
  box-shadow:
    inset 0 1px 2px rgba(255, 255, 255, 0.6),
    0 8px 30px rgba(192, 192, 192, 0.4),
    0 4px 15px rgba(255, 255, 255, 0.3) !important;
}

.package-card:nth-child(3) .save-badge {
  /* Gold Badge */
  background: linear-gradient(135deg, #ffd700 0%, #ffb300 100%);
  color: #1a1a1a;
  box-shadow:
    inset 0 1px 2px rgba(255, 255, 255, 0.8),
    0 8px 30px rgba(255, 179, 0, 0.4),
    0 4px 15px rgba(255, 255, 255, 0.4) !important;
}

.package-btn {
  background: linear-gradient(45deg, #f46523, #ff9800);
  border: none;
  border-radius: 50px;
  color: white;
  padding: 13px 28px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(244, 101, 35, 0.4);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  width: 100%;
}

.package-btn:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(244, 101, 35, 0.5);
}

.package-btn:active {
  transform: translateY(-2px);
}

@media (max-width: 992px) {
  .packages-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
  }

  .section-title {
    font-size: 48px;
    margin-bottom: 64px;
  }

  .package-card {
    padding: 32px 26px;
    height: 480px;
  }
}

@media (max-width: 768px) {
  .packages {
    padding: 80px 0;
  }

  .packages-grid {
    gap: 24px;
    margin-bottom: 80px;
  }

  .section-title {
    font-size: 40px;
    margin-bottom: 48px;
  }

  .package-card {
    padding: 28px 22px;
    height: 460px;
  }

  .package-header {
    height: auto;
  }

  .package-name {
    font-size: 24px;
    margin-top: 25px;
  }

  .price {
    font-size: 46px;
  }

  .period {
    font-size: 16px;
  }

  .feature-item {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .check-icon,
  .plus-icon {
    width: 14px;
    height: 14px;
    min-width: 14px;
  }

  .save-badge {
    right: 24px;
    min-width: 85px;
    padding: 14px 16px;
    top: -22px;
  }
}

@media (max-width: 480px) {
  .packages-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .section-title {
    font-size: 36px;
  }

  .package-card {
    padding: 22px 18px;
    height: 440px;
  }

  .package-name {
    font-size: 22px;
    margin-bottom: 4px;
  }

  .package-frequency {
    font-size: 13px;
    padding: 3px 10px;
  }

  .price {
    font-size: 40px;
  }

  .period {
    font-size: 15px;
  }

  .features-list {
    margin: 10px 0;
    padding: 0 3px;
  }

  .feature-item {
    font-size: 11px;
    margin-bottom: 5px;
    gap: 6px;
  }

  .check-icon,
  .plus-icon {
    width: 13px;
    height: 13px;
    min-width: 13px;
  }

  .package-btn {
    padding: 12px 20px;
    font-size: 15px;
  }

  .save-badge {
    padding: 12px 14px;
    min-width: 80px;
    top: -20px;
  }

  .save-label {
    font-size: 9px;
  }

  .save-value {
    font-size: 14px;
  }

  .popular-badge {
    padding: 8px 20px;
    font-size: 11px;
    top: -14px;
  }
}

@media (max-width: 360px) {
  .package-card {
    padding: 18px 14px;
    height: 420px;
  }
  .package-name {
    font-size: 20px;
  }
  .price {
    font-size: 36px;
  }
  .feature-item {
    font-size: 10px;
  }
  .package-btn {
    padding: 11px 18px;
    font-size: 14px;
  }
}
</style>
